import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import api from "../utils/api";
import GameTable from "./GameTable";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
const Games = () => {
  const [selectedAthikaram, setSelectedAthikaram] = useState([]);
  const [athikaramList, setAthikaramList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [formData, setFormData] = useState({
    gameName: "",
    noOfKurals: "",
    duration: "",
    athikaram: "",
    isActive: false,
    fetchByKuralNos: false,
    kuralNos: "",
    isDisabled: false,
    isDashboard: false,
    gameLevel: "1", // Add this line with default value
  });

  const [errors, setErrors] = useState({
    gameName: "",
    noOfKurals: "",
    duration: "",
    athikaram: "",
    gameLevel: "", // Add this line
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchAthikaramList = useCallback(async () => {
    try {
      const response = await api.get(
        "/api/kural/index.php?distinct_athikarams=true"
      );
      // Check if response.data is an array (the API returns an array of objects)
      if (Array.isArray(response.data)) {
        setAthikaramList(response.data);
        // Fallback for the old format if needed
        const list = response.data;
        // Extract all athikaram values into a single array
        const allAthikaramValues = list.map((item) =>
          typeof item === "object" ? item.athikaram : item
        );
        setAthikaramList(allAthikaramValues);
      } else if (response.data && Array.isArray(response.data.athikaram)) {
        setAthikaramList([]);
      }
    } catch (error) {
      console.error("Error fetching athikaram list:", error);
    }
  }, []);

  // Call fetchAthikaramList when component mounts
  useEffect(() => {
    fetchAthikaramList();
  }, [fetchAthikaramList]);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "gameName":
        if (!value.trim()) error = "Game name is required";
        else if (value.length < 3)
          error = "Game name must be at least 3 characters";
        break;
      case "noOfKurals":
        if (!value) error = "Number of Kurals is required";
        else if (value < 1) error = "Number of Kurals must be at least 1";
        else if (value > 10) error = "Number of Kurals cannot exceed 10";
        break;
      case "duration":
        if (!value) error = "Duration is required";
        else if (value < 1) error = "Duration must be at least 1 minute";
        else if (value > 10) error = "Duration cannot exceed 10 minutes";
        break;
      case "athikaram":
        if (!value.length) error = "Please select at least one Athikaram";
        break;
      case "gameLevel":
        if (!value) error = "Game level is required";
        break;
      default:
        break;
    }
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Validate the field
    const error = validateField(name, newValue);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate all fields
    const newErrors = {
      gameName: validateField("gameName", formData.gameName),
      noOfKurals: validateField("noOfKurals", formData.noOfKurals),
      duration: validateField("duration", formData.duration),
      athikaram: validateField("athikaram", formData.athikaram),
      gameLevel: validateField("gameLevel", formData.gameLevel), // Add this line
    };

    setErrors(newErrors);

    // Check if there are any errors
    if (Object.values(newErrors).some((error) => error !== "")) {
      return;
    }

    const selectedValues = selectedAthikaram.map((option) => option.value);
    console.log("Selected Options: ", selectedValues);

    // Create a new form data object with athikaram as comma-separated string
    const submissionData = {
      ...formData,
      athikaram: selectedValues.join(","),
      createdBy: "Cholan",
      updatedBy: "Cholan",
      ...(isEditMode && selectedGame ? { id: selectedGame.id } : {}),
    };

    // Check if it's an edit operation
    if (isEditMode) {
      try {
        const response = await api.put(`/api/game/`, submissionData);
        console.log("Responds:", response);
        if (response.status === 200) {
          Swal.fire({
            title: "Game Updated!",
            text: response.data.message,
            icon: "success",
          });
          // Fetch updated user list instead of page reload
        }
      } catch (error) {
        console.error("Error:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to update Game",
          icon: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
      setIsModalOpen(false);
      return;
    }

    console.log("Form submitted:", submissionData);

    try {
      const response = await api.post("/api/game/", submissionData);
      console.log("Responds:", response);
      if (response.status === 201) {
        Swal.fire({
          title: "Game Created!",
          text: response.data.message,
          icon: "success",
        });
        // Fetch updated user list instead of page reload
      }
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to create Game",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
    setIsModalOpen(false);
  };

  const toggleModal = () => {
    if (!isModalOpen) {
      resetForm();
    }
    setIsModalOpen(!isModalOpen);
    setIsEditMode(false);
  };

  const resetForm = () => {
    setFormData({
      gameName: "",
      noOfKurals: "",
      duration: "",
      athikaram: "",
      isActive: false,
      fetchByKuralNos: false,
      kuralNos: "",
      isDisabled: false,
      isDashboard: false,
      gameLevel: "1", // Add this line with default value
    });
    setSelectedAthikaram([]);
    setErrors({
      gameName: "",
      noOfKurals: "",
      duration: "",
      athikaram: "",
      gameLevel: "", // Add this line
    });
  };

  const handleEdit = (game) => {
    setIsEditMode(true);
    setSelectedGame(game);
    const selectedAthikaramArray = game.athikaram.split(",").map((item) => ({
      value: item.trim(),
      label: item.trim(),
    }));
    setFormData({
      ...game,
      noOfKurals: game.noOfKurals.toString(),
      duration: game.duration.toString(),
      athikaram: selectedAthikaramArray,
    });
    console.log("Selected Options-->: ", selectedAthikaramArray);
    setSelectedAthikaram(selectedAthikaramArray);
    setIsModalOpen(true);
  };

  // Handle change in selected options
  const handleSelectChange = (selected) => {
    setSelectedAthikaram(selected || []);
    console.log("Selected Options: ", selected);
    // Update the form data
    setFormData((prev) => ({
      ...prev,
      athikaram: selected.map((option) => option.value),
    }));
    // Validate athikaram selection
    const error = validateField("athikaram", selected);
    setErrors((prev) => ({
      ...prev,
      athikaram: error,
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Games Management</h2>
          <button
            onClick={toggleModal}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add New Game
          </button>
        </div>

        <GameTable onEdit={handleEdit} />

        {/* Modal for adding/editing games - Enhanced */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-indigo-600 text-white p-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">
                  {isEditMode ? "Edit Game" : "Add New Game"}
                </h2>
                <button
                  onClick={toggleModal}
                  className="text-white hover:text-gray-200 focus:outline-none"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Game Name */}
                  <div>
                    <label
                      htmlFor="gameName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Game Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="gameName"
                      name="gameName"
                      value={formData.gameName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 border ${errors.gameName ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200`}
                      placeholder="Enter game name"
                    />
                    {errors.gameName && (
                      <p className="mt-1 text-sm text-red-600">{errors.gameName}</p>
                    )}
                  </div>

                  {/* Game Level */}
                  <div>
                    <label
                      htmlFor="gameLevel"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Game Level <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="gameLevel"
                      name="gameLevel"
                      value={formData.gameLevel || "1"}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    >
                      <option value="1">Level 1 (Easy)</option>
                      <option value="2">Level 2 (Medium)</option>
                      <option value="3">Level 3 (Hard)</option>
                    </select>
                    {errors.gameLevel && (
                      <p className="mt-1 text-sm text-red-600">{errors.gameLevel}</p>
                    )}
                  </div>

                  {/* No of Kurals */}
                  <div>
                    <label
                      htmlFor="noOfKurals"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      No. of Kurals <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="noOfKurals"
                      name="noOfKurals"
                      value={formData.noOfKurals}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 border ${errors.noOfKurals ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200`}
                      placeholder="Enter number of kurals"
                    />
                    {errors.noOfKurals && (
                      <p className="mt-1 text-sm text-red-600">{errors.noOfKurals}</p>
                    )}
                  </div>

                  {/* Duration */}
                  <div>
                    <label
                      htmlFor="duration"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Duration (minutes) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="duration"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 border ${errors.duration ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200`}
                      placeholder="Enter duration in minutes"
                    />
                    {errors.duration && (
                      <p className="mt-1 text-sm text-red-600">{errors.duration}</p>
                    )}
                  </div>
                </div>

                {/* Athikaram Selection */}
                <div className="mb-6">
                  <label
                    htmlFor="athikaram"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Athikaram <span className="text-red-500">*</span>
                  </label>
                  <MultiSelectDropdown
                    options={athikaramList}
                    value={selectedAthikaram}
                    onChange={handleSelectChange}
                    placeholder="Select Athikaram"
                  />
                  {errors.athikaram && (
                    <p className="mt-1 text-sm text-red-600">{errors.athikaram}</p>
                  )}
                </div>

                {/* Game Settings */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-4">Game Settings</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Active Status */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="isActive"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Active Status
                      </label>
                    </div>

                    {/* Fetch By Kural Nos */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="fetchByKuralNos"
                        name="fetchByKuralNos"
                        checked={formData.fetchByKuralNos}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="fetchByKuralNos"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Fetch By Kural Nos
                      </label>
                    </div>

                    {/* Disabled */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isDisabled"
                        name="isDisabled"
                        checked={formData.isDisabled}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="isDisabled"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Disabled
                      </label>
                    </div>

                    {/* Show on Dashboard */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isDashboard"
                        name="isDashboard"
                        checked={formData.isDashboard}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="isDashboard"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Show on Dashboard
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={toggleModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-md text-white font-medium ${isSubmitting ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"} focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {isEditMode ? "Updating..." : "Creating..."}
                      </>
                    ) : isEditMode ? (
                      "Update Game"
                    ) : (
                      "Create Game"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Games;
